import { Flex, Modal, Text } from "@mantine/core";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import VesselLogoThumbnail from "src/components/pages/Settings/Miscellaneous/CheckLists/VesselLogoThumbnail";
import { useGetApi, useIsOnline, useThemeFlags } from "src/hooks";
import { type ICheckList } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setCheckList,
  setCheckListsModalProps,
} from "src/redux/slices/generatorSlice";
import { getYachtId } from "src/utils";

import { setEditing } from "../../../redux/slices/entrySlice";
import { setCheckLists } from "../../../redux/slices/generatorSlice";
import {
  clearChecklistsFromFlags,
  EChecklistType,
  filterChecklists,
  formatChecklistSuffix,
  mergeChecklistWithTemplate,
  shouldSyncChecklist,
} from "./checklists.utils";
import CheckListsForm from "./CheckListsForm/CheckListsForm";
import CheckListsList from "./CheckListsList";
import styles from "./ChecklistsModal.module.scss";
import { useTriggerAutoSave } from "src/generator/hooks/useTriggerAutoSave";

const mapChecklists = (data: any) =>
  clearChecklistsFromFlags(data?.list ?? [], [
    "changed",
    "hidden",
    "naChanged",
  ]);

type Props =
  | { type: "summary"; entryid?: any }
  | { type: "entry"; entryid?: any };

const CheckListsModal = (props: Props) => {
  const { logbookId, lbeidOrPartId } = useParams();
  const dispatch = useDispatch();
  const flags = useThemeFlags();
  const setValue = useFormContext()?.setValue;
  const { opened, id, cid, selectedIndex, summaryChecklists } = useSelector(
    state => state.generatorSlice.checkListsModalProps,
  );
  const readonlyChecklists = useSelector(
    state => state.entriesGeneratorSlice.checkLists,
  );
  const checkLists = useSelector(
    state => state.generatorSlice.lbentry.checklists,
  );
  const { sections } = useSelector(state => state.generatorSlice.lbentry);
  const { offlineChecklistsCopy } = useSelector(state => state.generatorSlice);
  const isOnline = useIsOnline();
  const [selectedCheckList, setSelecedCheckList] = useState<
    (ICheckList & { selectedItemIndex: number | null }) | undefined
  >();
  const [resetSearch, setResetSearch] = useState(0);
  const [checklistType, setChecklistType] = useState<EChecklistType | null>(
    props.type === "summary" ? null : EChecklistType.PROGRESS,
  );

  const initCellRender = useRef(true);
  const formRef = useRef(null);

  const {
    data,
    refetch: fetchChecklists,
    isLoading,
  } = useGetApi({
    request: "get-checklists",
    body: { params: { yachtid: getYachtId(), lbid: logbookId } },
    fetchOnInit: false,
    mapper: mapChecklists,
  });

  const triggerAutoSave = useTriggerAutoSave();

  useEffect(() => {
    if (window.api && offlineChecklistsCopy.length > 0 && isOnline) {
      dispatch(setCheckLists(offlineChecklistsCopy));
      dispatch(setEditing(false));
      setTimeout(() => dispatch(setEditing(true)), 100);
    }
  }, [dispatch, isOnline, offlineChecklistsCopy, sections]);

  useEffect(() => {
    if (opened && props.type !== "summary") {
      initCellRender.current = true;
      setSelecedCheckList(undefined);
      void fetchChecklists();
    }
    if (opened && checkLists) {
      setResetSearch(Math.random());
    }
  }, [fetchChecklists, opened, props, checkLists, summaryChecklists, cid]);

  const latestEntry = useSelector(state => state.generatorSlice.latestEntry);

  useEffect(() => {
    if (!opened || !data || !Array.isArray(checkLists)) return;

    data.forEach(template => {
      const existing = checkLists.find(c => c.cid === template.cid);
      if (!existing) {
        dispatch(setCheckList(template));
        return;
      }
      if (!shouldSyncChecklist(existing, template)) return;

      const synced = mergeChecklistWithTemplate(existing, template);

      dispatch(setCheckList(synced));
      triggerAutoSave();
    });
  }, [data, opened, checkLists, dispatch]);

  const tableData = useMemo(() => {
    let returnedChecklsits = [];
    if (summaryChecklists) returnedChecklsits = summaryChecklists;
    else if (props.type === "summary") returnedChecklsits = readonlyChecklists;
    else returnedChecklsits = checkLists;
    return checklistType !== null
      ? filterChecklists(returnedChecklsits, checklistType)
      : returnedChecklsits;
  }, [
    checkLists,
    props,
    readonlyChecklists,
    summaryChecklists,
    checklistType,
    selectedCheckList,
  ]);

  const [searchedData, setSearchedData] = useState(tableData);

  useEffect(() => {
    if (opened && tableData) {
      setSearchedData(tableData);
    }
  }, [tableData, opened]);

  useEffect(() => {
    if (id && cid && tableData && initCellRender.current) {
      initCellRender.current = false;
      const checkList = tableData.find(i => i.cid === cid);
      if (checkList)
        setSelecedCheckList({
          ...checkList,
          selectedItemIndex: selectedIndex ?? null,
        });
    }
  }, [id, cid, tableData, selectedIndex]);

  const handleClose = useCallback(() => {
    // Save if there's a selected checklist
    if (formRef.current && selectedCheckList) {
      (formRef.current as any).save();
    }

    // Close modal
    dispatch(
      setCheckListsModalProps({
        opened: false,
      }),
    );

    // Clear state after animation
    setTimeout(() => {
      setSelecedCheckList(undefined);
      dispatch(
        setCheckListsModalProps({
          opened: false,
          id: undefined,
          cid: undefined,
          selectedIndex: null,
        }),
      );
    }, 200);
  }, [dispatch, selectedCheckList]);

  const handleBack = useCallback(() => {
    // Save the checklist and go back to list
    if (formRef.current) {
      (formRef.current as any).save();
    }

    // Clear the selected checklist from the cell
    if (id && setValue) {
      setValue(id, undefined);
    }

    // Clear the modal props
    dispatch(
      setCheckListsModalProps({
        opened: true,
        id: undefined,
        cid: undefined,
        selectedIndex: null,
      }),
    );

    // Go back to the list view
    setSelecedCheckList(undefined);
  }, [id, setValue, dispatch]);

  const getTitleAndDesc = () => {
    if (!selectedCheckList) return { title: "", description: "" };
    if (
      selectedCheckList.selectedItemIndex !== null &&
      selectedCheckList.selectedItemIndex !== undefined
    ) {
      const item =
        selectedCheckList.data.list[selectedCheckList.selectedItemIndex];
      const { name, suffix } = selectedCheckList;
      return {
        title: ` / ${name}${formatChecklistSuffix(suffix)} / ${item.name}`,
        description: item.desc ?? "",
      };
    }
    return {
      title: ` / ${selectedCheckList.name}`,
      description: selectedCheckList.description || "",
    };
  };

  const renderTitle = useCallback(() => {
    let titleProps = {};
    if (selectedCheckList) {
      titleProps = {
        onClick: handleBack,
        style: {},
        className: styles.title,
      };
    }
    const { title } = getTitleAndDesc();
    return (
      <Flex align="center" gap="xs">
        <VesselLogoThumbnail size={22} />
        <Text {...titleProps} component="span" c="text.0">
          Checklists
        </Text>
        {selectedCheckList && (
          <Text component="span" c="text.0">
            {title}
          </Text>
        )}
      </Flex>
    );
  }, [handleBack, selectedCheckList]);

  if (selectedCheckList) {
    selectedCheckList.currentLogbook = logbookId;
    const routeEntryId =
      typeof lbeidOrPartId === "string" && lbeidOrPartId.includes("new=")
        ? lbeidOrPartId
        : undefined;
    selectedCheckList.currentEntryId =
      routeEntryId || latestEntry?.id || undefined;
  }

  const isDisabled =
    props.type === "summary" || checklistType === EChecklistType.COMPLETED;

  const handleModalClose = useCallback(() => {
    if (selectedCheckList) {
      handleBack();
    } else {
      handleClose();
    }
  }, [selectedCheckList, handleBack, handleClose]);

  if (flags.disableChecklists) return null;

  return (
    <Modal
      opened={opened}
      zIndex={10}
      onClose={handleModalClose}
      title={renderTitle()}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody,
      }}
      size="xl">
      {selectedCheckList ? (
        <CheckListsForm
          {...selectedCheckList}
          ref={formRef}
          reset={handleBack}
          disabled={isDisabled}
          close={handleClose}
          renderedDesc={getTitleAndDesc().description}
        />
      ) : (
        <CheckListsList
          data={searchedData}
          defaultData={tableData}
          setSearchedData={setSearchedData}
          currentLbid={logbookId}
          setCheckList={setSelecedCheckList}
          checklistType={checklistType}
          setChecklistType={setChecklistType}
          disableCopy={isDisabled}
          viewOnly={props.type === "summary"}
          loading={isLoading}
          refetch={fetchChecklists}
          resetSearch={resetSearch}
          setResetSearch={setResetSearch}
        />
      )}
    </Modal>
  );
};

export default memo(CheckListsModal);
